/* eslint-disable react/no-danger */
import { Button } from "@Modules/common";
import { ButtonV2 } from "@Modules/common/ButtonV2";
import { DropdownSelector, TextInput } from "@Modules/common/Form";
import { IDropdownSelector } from "@Modules/common/Form/FormInputs/DropdownSelector/IDropdownSelector";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    BUTTON_DESIGNS,
    FORM_FIELDS_KEYS,
    REACTION_TYPES,
    STORAGE_KEYS,
    createAction,
    getUserAccessToken,
    isBrowser,
    isEmpty,
    isIndia,
    useForm,
} from "@Utils";
import { BsHeartFill } from "@react-icons/all-files/bs/BsHeartFill";
import { IoShareSocialSharp } from "@react-icons/all-files/io5/IoShareSocialSharp";
import { MdModeComment } from "@react-icons/all-files/md/MdModeComment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { classNames } from "repoV2/utils/common/render/classNames";
import { getLocalStorageItem } from "repoV2/utils/common/storage/getterAndSetters";
import { parseDateTime } from "@Utils/parseDateTime";
import { BlogPostComments } from "../BlogPostComments";
import styles from "./BlogPostBody.module.scss";
import { IBlogPostBody } from "./IBlogPostBody";

export const BlogPostBody = ({
    data,
    hostName,
    openBlogVisitorPopup,
    openSharePopup,
    userReactionType,
    likeCount,
    onLikeClick,
    commentCount,
    commentClick,
    onCreateComment,
    preview,
    handlePreviewPopup,
}: IBlogPostBody.IProps) => {
    const dispatch = useDispatch();

    const isLoggedin = !!getUserAccessToken();

    let subscribed: null | string = null;
    if (isBrowser()) {
        subscribed = getLocalStorageItem(STORAGE_KEYS.BLOG_SUBSCRIPTION_EMAIL);
    }

    const { formData, createForm, setFormData, formIsValid } =
        useForm("blogSubscriber");

    const emailField = formData.fields[FORM_FIELDS_KEYS.email];
    const fullNameField = formData.fields[FORM_FIELDS_KEYS.full_name];
    const phoneField = formData.fields[FORM_FIELDS_KEYS.phone];
    const countryCodeField = formData.fields[FORM_FIELDS_KEYS.country_code];
    const selctedCountryCode = countryCodeField?.options?.find(
        (option: IDropdownSelector.Option) =>
            countryCodeField?.value === option.dial_code
    );
    const countryField = formData.fields[FORM_FIELDS_KEYS.country];

    useEffect(() => {
        createForm({
            fields: [
                FORM_FIELDS_KEYS.email,
                FORM_FIELDS_KEYS.full_name,
                FORM_FIELDS_KEYS.phone,
                FORM_FIELDS_KEYS.country,
                FORM_FIELDS_KEYS.country_code,
            ].map(fieldKey => ({ key: fieldKey })),
        });
    }, []);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        setFormData({
            fields: [{ key: e.target.name, value: e.target.value }],
            fieldMethodsExtraData: { isInIndia: isIndia(countryField?.value) },
        });
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (preview) {
            return;
        }
        setFormData({
            fields: [{ key: e.target.name, showError: true }],
        });
    };

    const handleCountryCodeChange = (
        option: IDropdownSelector.Option | null
    ) => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        setFormData({
            fields: [
                {
                    key: FORM_FIELDS_KEYS.country_code,
                    value: option?.dial_code,
                },
                {
                    key: FORM_FIELDS_KEYS.country,
                    value: option?.name,
                },
                {
                    key: FORM_FIELDS_KEYS.phone,
                    showError: true,
                },
            ],
            fieldMethodsExtraData: { isInIndia: isIndia(option?.name) },
        });
    };

    const [subscribeClicked, setSubscribeClicked] = useState(false);
    const onSubscribeClick = () => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        if (
            !formIsValid({
                fieldMethodsExtraData: {
                    isInIndia: isIndia(countryField?.value),
                },
            })
        ) {
            alert("Please enter valid details!");
            return;
        }
        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.SUBSCRIBE_TO_BLOG,
                urlArgs: {
                    hostName,
                },
                payload: {
                    email: emailField?.value,
                    full_name: fullNameField?.value?.trim(),
                    country_code: countryCodeField?.value,
                    country: countryField?.value,
                },
                successCallback: () => {
                    setSubscribeClicked(true);
                    alert("You are subscribed!");
                },
            })
        );
    };

    return (
        <div className={styles.container}>
            <div
                className={classNames(
                    styles.boldText,
                    styles.title,
                    styles.sectionGap,
                    styles.breakWord
                )}
            >
                {data?.title}
            </div>
            <div className={styles.sectionGap}>
                Published by{" "}
                <span className={classNames(styles.boldText, styles.breakWord)}>
                    {data?.author_name}
                </span>
            </div>
            <div>
                {data?.created_at
                    ? parseDateTime(data?.created_at, "MMM dd, yyyy")
                    : null}
            </div>
            <hr />
            {data?.thumbnail_image ? (
                <img
                    src={data?.thumbnail_image}
                    className={styles.image}
                    alt={data?.title}
                />
            ) : null}
            <div
                className={styles.postContent}
                dangerouslySetInnerHTML={{ __html: data?.content || "" }}
            />

            {!subscribed && !subscribeClicked && !isLoggedin ? (
                <>
                    <hr className={styles.subscribeSectionBottomDivider} />
                    <div
                        className={classNames(
                            styles.boldText,
                            styles.title,
                            styles.sectionGap
                        )}
                    >
                        Enjoy these posts? Subscribe for more
                    </div>
                    <div className={styles.sectionGap}>
                        {!isEmpty(Object.keys(formData.fields)) ? (
                            <>
                                <TextInput
                                    name={fullNameField.key}
                                    type="text"
                                    placeholder="Full Name"
                                    value={fullNameField?.value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={
                                        fullNameField?.showError &&
                                        fullNameField?.error
                                    }
                                />
                                <div className={styles.inputError}>
                                    {fullNameField?.showError
                                        ? fullNameField?.errorMessage
                                        : ""}
                                </div>
                                <TextInput
                                    name={emailField.key}
                                    type="email"
                                    placeholder="Enter your email id"
                                    value={emailField?.value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={
                                        emailField?.showError &&
                                        emailField?.error
                                    }
                                />
                                <div className={styles.inputError}>
                                    {emailField?.showError
                                        ? emailField?.errorMessage
                                        : ""}
                                </div>
                                <div
                                    className={
                                        styles.phoneCountryFieldsAndErrorContainer
                                    }
                                >
                                    <div
                                        className={
                                            styles.phoneCountryFieldsContainer
                                        }
                                    >
                                        <DropdownSelector
                                            value={selctedCountryCode}
                                            onChange={handleCountryCodeChange}
                                            options={
                                                countryCodeField?.options as Array<IDropdownSelector.Option>
                                            }
                                            className={
                                                styles.countryCodeDropdown
                                            }
                                        />
                                        <TextInput
                                            name={phoneField.key}
                                            type="tel"
                                            placeholder="Phone Number"
                                            value={phoneField?.value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            error={
                                                phoneField?.showError &&
                                                phoneField?.error
                                            }
                                            className={styles.phoneField}
                                        />
                                    </div>
                                    <div className={styles.inputError}>
                                        {phoneField?.showError
                                            ? phoneField?.errorMessage
                                            : ""}
                                    </div>
                                </div>
                            </>
                        ) : null}
                        <ButtonV2
                            applyTheme
                            className={styles.subscribeButton}
                            onClick={onSubscribeClick}
                        >
                            Subscribe
                        </ButtonV2>
                    </div>
                </>
            ) : null}
            <hr />
            <div>
                <div>
                    <div className={styles.postStatsContainer}>
                        <Button
                            design={BUTTON_DESIGNS.PLAIN_TEXT}
                            content={
                                <>
                                    <BsHeartFill
                                        color={
                                            userReactionType ===
                                            REACTION_TYPES.LIKE
                                                ? "#ff6262"
                                                : "grey"
                                        }
                                        size="1rem"
                                    />
                                    <div className={styles.statCount}>
                                        {likeCount}
                                    </div>
                                </>
                            }
                            className={styles.postStatContainer}
                            onClick={onLikeClick}
                        />
                        <Button
                            design={BUTTON_DESIGNS.PLAIN_TEXT}
                            content={
                                <>
                                    <MdModeComment size="1rem" />
                                    <div className={styles.statCount}>
                                        {commentCount}
                                    </div>
                                </>
                            }
                            className={styles.postStatContainer}
                            onClick={commentClick}
                        />
                        <Button
                            design={BUTTON_DESIGNS.PLAIN_TEXT}
                            content={<IoShareSocialSharp size="1rem" />}
                            className={styles.postStatContainer}
                            onClick={openSharePopup}
                        />
                    </div>
                </div>
                <hr className={styles.postStatsContainer} />
                <div id="comments" className={styles.commentsUrlHash} />
                <BlogPostComments
                    data={data}
                    onCreateComment={onCreateComment}
                    openBlogVisitorPopup={openBlogVisitorPopup}
                    preview={preview}
                    handlePreviewPopup={handlePreviewPopup}
                />
            </div>
        </div>
    );
};

BlogPostBody.defaultProps = {};
